import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { CreatableTicketCategoryV2Dto, TicketRequest, UserDto } from "@/api/types";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { FormVideoInput } from "@/components/Form/FormVideoInput";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import {
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
  createVideoSizeRule,
  createVideoTypeRule,
} from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useTranslation } from "@/translations";

import { getVisibilityOptionIcon, getVisibilityOptionLabelKey, usePotentialDuplicateTickets } from "../helpers";
import type { CreateTicketModalFormValues } from "../types";
import { CreateTicketCollectiveTicketCard } from "./CreateTicketCollectiveTicketCard";
import { CreateTicketModalOptionButton } from "./CreateTicketModalOptionButton";

type SelectCategoryStepContentProps = {
  onSelectTicketCategory: (category: CreatableTicketCategoryV2Dto) => void;
};

const SelectCategoryStepContent = ({ onSelectTicketCategory }: SelectCategoryStepContentProps): React.ReactNode => {
  const { t } = useTranslation();
  const form = useFormContext<CreateTicketModalFormValues>();
  const currReporter = useWatch({
    name: "reporter",
    control: form.control,
  });
  const sessionUser = useSessionUser();

  const ticketCategoryQueries = useTicketCategoryQueries();
  const {
    data: categoriesData,
    isPending: isPendingCategoriesData,
    isError: isErrorCategoriesData,
  } = useQuery(
    ticketCategoryQueries.listCategoryCreatables({
      forUserId: currReporter?.id || sessionUser.id,
    }),
  );

  // Reset category when this step is mounted
  useEffect(() => {
    form.setValue("category", undefined);
  }, [form]);

  const categories = categoriesData?.categories || [];

  return (
    <section className="flex h-full flex-col gap-space-sm">
      <span className="text-body-emphasis">{t("page.resident-tickets.create-modal.form.category.title")}</span>
      {isPendingCategoriesData && (
        <section className="flex flex-1 items-center">
          <FullSizeLoader size="sm" noDelay />
        </section>
      )}
      {!isPendingCategoriesData && !isErrorCategoriesData && (
        <ul className="relative flex flex-col gap-space-sm">
          {categories.map((currCategory) => (
            <li className="w-full" key={currCategory.id}>
              <CreateTicketModalOptionButton
                data-testid="ticket-category-item"
                title={currCategory.name}
                onSelect={() => onSelectTicketCategory(currCategory)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const AddAttachmentsStepContent = (): React.ReactNode => {
  const { t } = useTranslation();
  const form = useFormContext<CreateTicketModalFormValues>();
  const postHog = usePostHog();

  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });

  const maxImages = 5;
  const maxVideos = 1;

  return (
    <Inline gap="md" wrap={false}>
      {(currVideos.length === 0 || currCategory?.categoryInfo?.requireImages) && (
        <FormField
          label={t("page.resident-tickets.create-modal.form.images.label", {
            maxAmount: maxImages,
          })}
          required={currCategory?.categoryInfo?.requireImages}
        >
          <FormImageInput<CreateTicketModalFormValues, "images">
            name="images"
            nOfImages={maxImages}
            rules={{
              validate: {
                size: createImageSizeRule({
                  t,
                  onError: () => postHog.capture("form_error_image_size_exceeded"),
                }),
                type: createImageTypeRule({ t }),
              },
            }}
          />
        </FormField>
      )}
      {currImages.length === 0 && currCategory?.categoryInfo?.allowVideos && (
        <FormField
          label={t("page.resident-tickets.create-modal.form.videos.label", {
            maxAmount: maxVideos,
          })}
        >
          <FormVideoInput<CreateTicketModalFormValues, "videos">
            name="videos"
            nOfVideos={maxVideos}
            rules={{
              validate: {
                size: createVideoSizeRule({
                  t,
                  onError: () => postHog.capture("form_error_video_size_exceeded"),
                }),
                type: createVideoTypeRule({ t }),
              },
            }}
          />
        </FormField>
      )}
    </Inline>
  );
};

const AddInformationStepContent = (): React.ReactNode => {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const form = useFormContext<CreateTicketModalFormValues>();
  const hasPermission = usePermission();

  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });
  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });
  const currTitle = useWatch({
    name: "title",
    control: form.control,
  });

  const ticketCategoryQueries = useTicketCategoryQueries();
  const canAssignTicket =
    currCategory && !currCategory.tree
      ? hasPermission((p) => p.hasTicketCategoryPermission(currCategory.id, (x) => x.canUpdateRequestStatus))
      : false;
  const {
    data: assignees = [],
    isFetching: isFetchingAssignees,
    isError: isErrorAssignees,
    error: errorAssignees,
  } = useQuery({
    ...ticketCategoryQueries.listAssignees(),
    enabled: sessionUser.isAdmin,
  });
  const defaultAssignee = assignees.find((assignee) => assignee.id === currCategory?.categoryInfo?.defaultAssignee?.id);

  // Set default assignee if it has not been modified by user
  useEffect(() => {
    if (defaultAssignee && !form.formState.dirtyFields.assignee) {
      form.setValue("assignee", defaultAssignee);
    }
  }, [defaultAssignee, form]);

  const getVisibilityOptionLabel = (visibility: TicketRequest["visibility"]) => {
    const translationKey = getVisibilityOptionLabelKey(visibility);

    if (visibility === "sharedWithBuilding") {
      return t(translationKey, {
        buildingName: sessionUser.address?.building?.name || "",
      });
    } else {
      return t(translationKey);
    }
  };

  const isDecisionTreeFlowSelected = currTree.path.length > 0;
  const hasMultipleBuildings = isDecisionTreeFlowSelected
    ? currTree.path[currTree.path.length - 1]?.nodeInfo?.projectHasMultipleBuildings
    : currCategory?.categoryInfo?.projectHasMultipleBuildings;
  const isLeafOtherNodeSelected =
    isDecisionTreeFlowSelected && currTree.path[currTree.path.length - 1].type === "leafOther";
  const canBePublic = isDecisionTreeFlowSelected
    ? currTree.path[currTree.path.length - 1]?.nodeInfo?.canBePublic
    : currCategory?.categoryInfo?.canBePublic;

  const optionsVisibility = useMemo(() => {
    const options: NonNullable<TicketRequest["visibility"]>[] = ["private"];

    if (hasMultipleBuildings) {
      options.push("sharedWithBuilding");
    }

    options.push("sharedWithCommunity");

    return options;
  }, [hasMultipleBuildings]);

  if (isErrorAssignees) {
    throw new Error(errorAssignees?.message);
  }

  const maxLengthTitle = 255;

  return (
    <Stack gap="sm">
      {currTree.path.length > 0 && !isLeafOtherNodeSelected && currTitle && (
        <h3 data-testid="generated-tree-ticket-title" className="mb-space-sm text-heading-3">
          {currTitle}
        </h3>
      )}
      {(currTree.path.length === 0 || isLeafOtherNodeSelected) && (
        <FormField label={t("page.resident-tickets.create-modal.form.subject.title")} required>
          <FormInput<CreateTicketModalFormValues>
            name="title"
            placeholder={t("page.resident-tickets.create-modal.form.subject.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.resident-tickets.create-modal.form.subject.title"),
              },
              maxLength: {
                message: t("components.form.error.max-length", {
                  length: maxLengthTitle,
                }),
                value: maxLengthTitle,
              },
            }}
          />
        </FormField>
      )}
      <FormField label={t("page.resident-tickets.create-modal.form.description.title")} required>
        <FormTextArea<CreateTicketModalFormValues>
          name="content"
          placeholder={t("page.resident-tickets.create-modal.form.description.placeholder")}
          rules={{
            validate: {
              required: createRequiredStringRule(t, "page.resident-tickets.create-modal.form.description.title"),
            },
          }}
        />
      </FormField>
      {canBePublic && (
        <FormField label={t("page.addresses.form.building")} required>
          <FormSelect<CreateTicketModalFormValues, (typeof optionsVisibility)[number]>
            name="visibility"
            items={optionsVisibility}
            keySelector={(x) => x}
            renderOption={(x) => (
              <Inline gap="sm" align="center" wrap={false}>
                {getVisibilityOptionIcon(x)}
                <span>{getVisibilityOptionLabel(x)}</span>
              </Inline>
            )}
            placeholder={t("page.address.form.building.placeholder")}
            usePortal
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.addresses.form.building"),
              }),
            }}
          />
        </FormField>
      )}
      {canAssignTicket && (
        <FormField label={t("page.resident-tickets.create-modal.form.assignee")}>
          <FormSelect<CreateTicketModalFormValues, UserDto>
            name="assignee"
            items={assignees}
            disabled={isFetchingAssignees || assignees.length === 0}
            emptyItem={t("page.resident-tickets.create-modal.form.unassigned")}
            keySelector={(x) => x.id}
            renderOption={(x) => x.fullName}
            usePortal
          />
        </FormField>
      )}
    </Stack>
  );
};

const AddContactInformationStepContent = (): React.ReactNode => {
  const { t } = useTranslation();

  return (
    <Stack gap="md">
      <p className="text-body">{t("page.resident-tickets.create-modal.description.add-contact-information")}</p>
      <Stack gap="xs">
        <FormField label={t("page.resident-tickets.create-modal.form.phone-number.title")} required>
          <FormInput<CreateTicketModalFormValues, "phoneNumber">
            name="phoneNumber"
            placeholder={t("page.resident-tickets.create-modal.form.phone-number.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.resident-tickets.create-modal.form.phone-number.title"),
              },
            }}
            autoFocus
          />
        </FormField>
        <p className="text-overline text-text-secondary">
          {t("page.resident-tickets.create-modal.form.phone-number.helper-text")}
        </p>
      </Stack>
    </Stack>
  );
};

const ConfirmStepContent = (): React.ReactNode => {
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);

  const { t } = useTranslation();
  const form = useFormContext<CreateTicketModalFormValues>();
  const sessionUser = useSessionUser();

  const currTitle = useWatch({
    name: "title",
    control: form.control,
  });
  const currDescription = useWatch({
    name: "content",
    control: form.control,
  });
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });
  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });
  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });
  const currVisibility = useWatch({
    name: "visibility",
    control: form.control,
  });
  const currPhoneNumber = useWatch({
    name: "phoneNumber",
    control: form.control,
  });
  const currAssignee = useWatch({
    name: "assignee",
    control: form.control,
  });
  const currReporter = useWatch({
    name: "reporter",
    control: form.control,
  });

  const getVisibilityOptionLabel = (visibility: TicketRequest["visibility"]) => {
    const translationKey = getVisibilityOptionLabelKey(visibility);

    if (visibility === "sharedWithBuilding") {
      return t(translationKey, {
        buildingName: sessionUser.address?.building?.name || "",
      });
    } else {
      return t(translationKey);
    }
  };

  // Should not happen, but added for type safety
  if (!currCategory) {
    return null;
  }

  const currLeafNode = currTree.path[currTree.path.length - 1];
  const introText =
    (currCategory.tree ? currLeafNode?.nodeInfo?.customIntroText : currCategory.categoryInfo?.customIntroText) ??
    t("page.resident-tickets.create-modal.intro-text");

  const isSignOffRegulationRequired = currCategory.tree
    ? currLeafNode?.nodeInfo?.requireSignOffRegulations
    : currCategory.categoryInfo?.requireSignOffRegulations;
  const regulations = currCategory.tree ? currLeafNode?.nodeInfo?.regulations : currCategory.categoryInfo?.regulations;
  const isPhoneNumberRequired = currCategory.tree
    ? currLeafNode?.nodeInfo?.requirePhoneNumber
    : currCategory.categoryInfo?.requirePhoneNumber;
  const isRegulationShown = !currReporter && isSignOffRegulationRequired && !!regulations;
  const isFollowUpInfoShown = !currReporter && Boolean(introText);
  const canEditPhoneNumber = currReporter ? Boolean(currReporter?.phoneNumber) : Boolean(sessionUser.phoneNumber);

  return (
    <section className="flex size-full flex-col gap-space-lg">
      <Box radius="md" background="secondary" padding="md">
        <Stack gap="md">
          <Stack gap="xs">
            <h3 className="text-heading-3">{currTitle}</h3>
            <p className="text-body whitespace-pre-line">{currDescription}</p>
          </Stack>
          {currImages.length > 0 && (
            <ol className="flex w-full flex-col gap-space-sm @sm:flex-row">
              {currImages.map((image, index) => (
                <li key={image.url} className="aspect-square overflow-hidden rounded-md @sm:max-h-card-media">
                  <img
                    className="size-full object-cover"
                    src={image.url}
                    alt={t("model.ticket.attachment.alt", { number: index + 1 })}
                  />
                </li>
              ))}
            </ol>
          )}
          {currVideos.length > 0 && (
            <ol className="flex w-full flex-col gap-space-sm @sm:flex-row">
              {currVideos.map((video) => (
                <li key={video.url} className="aspect-video w-full max-w-measure-default overflow-hidden rounded-md">
                  <video src={video.url} className="size-full object-cover" controls />
                </li>
              ))}
            </ol>
          )}
          <Stack gap="sm">
            <Stack gap="xs">
              <span className="text-overline-emphasis text-text-secondary">
                {t("page.resident-tickets.create-modal.form.visibility")}
              </span>
              <Inline gap="sm" align="center" wrap={false}>
                {getVisibilityOptionIcon(currVisibility)}
                <span className="text-body">{getVisibilityOptionLabel(currVisibility)}</span>
              </Inline>
            </Stack>
            {currAssignee && (
              <Stack gap="xs">
                <span className="text-overline-emphasis text-text-secondary">
                  {t("page.resident-tickets.create-modal.form.assignee")}
                </span>
                <Inline gap="sm" align="center" wrap={false}>
                  <span className="text-body">{currAssignee?.fullName}</span>
                </Inline>
              </Stack>
            )}
            {isPhoneNumberRequired && (
              <Stack gap="xs">
                <span className="text-overline-emphasis text-text-secondary">
                  {currReporter
                    ? t("page.resident-tickets.create-modal.form.reporter-phone-number")
                    : t("page.resident-tickets.create-modal.form.phone-number")}
                </span>
                {isEditingPhoneNumber ? (
                  <Inline gap="sm" align="center" wrap={false}>
                    <FormInput<CreateTicketModalFormValues, "phoneNumber">
                      id="phone-number-input"
                      name="phoneNumber"
                      placeholder={t("page.resident-tickets.create-modal.form.phone-number.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(
                            t,
                            "page.resident-tickets.create-modal.form.phone-number.title",
                          ),
                        },
                      }}
                      autoFocus
                    />
                    <Button variant="ghostPrimary" size="sm" onClick={() => setIsEditingPhoneNumber(false)}>
                      {t("common.action.save")}
                    </Button>
                  </Inline>
                ) : (
                  <Inline gap="sm" align="center" wrap={false}>
                    <span className="text-body">{currPhoneNumber}</span>
                    {canEditPhoneNumber && (
                      <Button
                        variant="ghostPrimary"
                        size="sm"
                        icon={<PencilLineIcon />}
                        onClick={() => setIsEditingPhoneNumber(true)}
                      >
                        {t("common.action.edit")}
                      </Button>
                    )}
                  </Inline>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      {isFollowUpInfoShown && (
        <Stack gap="sm">
          <h4 className="text-heading-4">{t("page.resident-tickets.create-modal.what-to-expect")}</h4>
          <MultiLineContent>{introText}</MultiLineContent>
        </Stack>
      )}
      {isRegulationShown && (
        <FormErrorWrapper pushToEnd name="isRegulationsAccepted">
          <FormCheckbox<CreateTicketModalFormValues>
            name="isRegulationsAccepted"
            label={t("page.resident-tickets.create-modal.form.regulations")}
            rules={{
              validate: {
                required: (value) => {
                  if (!value) {
                    return t("page.resident-tickets.create-modal.form.regulations.error");
                  }
                },
              },
            }}
          />
          <nav className="ml-space-lg w-fit">
            <Button type="link" target="_blank" variant="ghostPrimary" href={regulations.url} isExternal>
              {t("page.resident-tickets.create-modal.form.regulations.link")}
            </Button>
          </nav>
        </FormErrorWrapper>
      )}
    </section>
  );
};

const ViewPotentialDuplicateTicketsStepContent = (): React.ReactNode => {
  const {
    data: potentialDuplicateTicketsData,
    isFetching: isFetchingPotentialDuplicateTicketsData,
    isError: isErrorPotentialDuplicateTicketsData,
    error: errorPotentialDuplicateTicketsData,
  } = usePotentialDuplicateTickets({
    isEnabled: false,
  });

  const potentialDuplicateTickets = useMemo(() => {
    return potentialDuplicateTicketsData?.pages?.flatMap((page) => page.items) || [];
  }, [potentialDuplicateTicketsData]);

  if (isFetchingPotentialDuplicateTicketsData) {
    return <FullSizeLoader withPadding />;
  }

  if (isErrorPotentialDuplicateTicketsData) {
    throw new Error(errorPotentialDuplicateTicketsData?.message);
  }

  return (
    <Stack gap="sm" as="ul">
      {potentialDuplicateTickets.map((ticket) => (
        <li key={ticket.id} className="w-full" data-testid="existing-ticket-item">
          <CreateTicketCollectiveTicketCard ticket={ticket} />
        </li>
      ))}
    </Stack>
  );
};

export const CreateTicketModalSharedSteps = {
  SelectCategory: SelectCategoryStepContent,
  ViewPotentialDuplicateTickets: ViewPotentialDuplicateTicketsStepContent,
  AddAttachments: AddAttachmentsStepContent,
  AddInformation: AddInformationStepContent,
  AddContactInformation: AddContactInformationStepContent,
  Confirm: ConfirmStepContent,
};
